import { LAUNCHPAD_PATH } from "@/features/home/hooks/use-home";
import { useConversations } from "@/api/hooks/use-conversations";
import { useQueryApi } from "@/api/hooks/use-query-api";
import { useDeleteConversation } from "@/api/hooks";
import { useConversationsStore } from "../store/conversations-store";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import { useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLeftSidebar } from "@/components/ui/sidebar";
import { isMobile } from "@/lib/is-mobile";
import { getModuleChatConfig, useActiveModule } from "@/modules";
import { useChatDockStore } from "@/features/chat-dock/store/chat-dock-store";

export const useSidebarConversations = () => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // ===== Modül kapsamı =====
  // Sohbeti açık bir modüldeysek liste sunucuda o modülün konuşmalarına
  // daralır (`?module=`) ve seçim dock'ta açılır; aksi hâlde (hub, /chat,
  // shared sayfalar) tüm konuşmalar görünür ve seçim /chat/:id'ye gider.
  const activeModule = useActiveModule();
  const isModuleScoped = Boolean(
    activeModule && getModuleChatConfig(activeModule)?.enabled
  );

  // ===== API Hooks (Existing) =====
  // ModuleRecentChats ile aynı parametreler → aynı cache
  const {
    data: conversationsData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversations(
    {
      limit: 12,
      sortBy: "-createdAt",
      ...(isModuleScoped && activeModule && { module: activeModule.id }),
    },
    { enabled: true }
  );

  const { setOpen } = useLeftSidebar();
  const { isLoading: isCreating } = useQueryApi();

  // ===== Delete API Hook =====
  const { deleteConversation } = useDeleteConversation();

  // ===== Store (Conversations Feature) =====
  const { setDeleting } = useConversationsStore();

  // ===== Error Handling: Chat List Error =====
  // Handle error when conversation list cannot be accessed
  useEffect(() => {
    if (error && !isLoading) {
      const errorResponse = (error as any)?.response;
      const errorData = errorResponse?.data;

      const errorMessage =
        errorData?.message ||
        errorData?.error?.message ||
        t("chat.conversationListError");

      // Show error toast
      showError(errorMessage, {
        title: t("chat.conversationListError"),
        actions: [
          {
            label: t("common.home"),
            action: () => navigate("/"),
          },
        ],
      });

      // Navigate to main root after showing error
      const timer = setTimeout(() => {
        navigate("/");
      }, 2000); // 2 saniye sonra yönlendir

      return () => clearTimeout(timer);
    }
  }, [error, isLoading, navigate, showError, t]);

  // ===== Process Data =====
  // Modül filtresi sunucuda uygulandı; burada yalnızca sayfalar düzleştirilir
  const conversations = useMemo(() => {
    if (!conversationsData?.pages) return [];

    // 🛡️ Defensive: Safe flatMap with validation
    return conversationsData.pages.flatMap((page) => {
      // Validate page structure before accessing result
      if (!page?.data?.result) {
        console.warn("Invalid page structure in conversations data:", page);
        return [];
      }
      return page.data.result;
    });
  }, [conversationsData]);

  // 🚫 REMOVED: No more dual store sync needed!

  // ===== Actions =====
  const handleChatSelect = (conversationId: string) => {
    if (isModuleScoped && activeModule) {
      // Modül içinde: konuşma sağ dock'ta açılır, sayfa değişmez
      const dock = useChatDockStore.getState();
      dock.setModuleChat(activeModule.id, conversationId);
      dock.open();
    } else {
      // 🚀 Navigation: Chat sayfasına yönlendir
      navigate(`/chat/${conversationId}`);
    }
    if (isMobile()) {
      setOpen(false);
    }
  };

  const handleChatDelete = async (conversationId: string): Promise<void> => {
    setDeleting(conversationId, true);

    const pathParts = location.pathname.split("/").filter(Boolean);
    const activePathConversationId =
      pathParts[0] === "chat" ? pathParts[1] : null;
    const isDeletingActiveConversation =
      activePathConversationId === conversationId;

    try {
      // Aktif chat silinirken önce route'tan çık, böylece message query observer'ları unmount olur.
      if (isDeletingActiveConversation) {
        navigate("/", { replace: true });
      }

      // 🚀 Real API call to delete conversation
      await deleteConversation(conversationId);

      // Dock'ta açıksa dock bağını temizle
      const dock = useChatDockStore.getState();
      for (const [moduleId, chatId] of Object.entries(dock.chatByModule)) {
        if (chatId === conversationId) {
          dock.setModuleChat(moduleId as keyof typeof dock.chatByModule, null);
        }
      }

      // ✅ Success feedback
      success(t("chat.deleteSuccess"), {
        title: t("common.success"),
      });

    } catch (deleteError: any) {
      const errorMessage = deleteError?.message || t("chat.deleteError");

      showError(errorMessage, {
        title: t("error.title"),
        actions: [
          {
            label: t("common.retry"),
            action: () => handleChatDelete(conversationId),
          },
        ],
      });
      throw deleteError;
    } finally {
      setDeleting(conversationId, false);
    }
  };

  const handleCreateChat = () => {
    // 🚀 Navigation: "Yeni sohbet" → Launchpad (modül seç, sohbet dock'ta başlar)
    navigate(LAUNCHPAD_PATH);

    // ✅ User feedback
    // success(t("chat.created"), {
    //   title: t("chat.newChatCreated"),
    // });
  };

  // ===== Return Interface =====
  return {
    // Data
    conversations,
    /** Liste aktif modüle daraltılmış mı (seçim dock'ta açılır). */
    isModuleScoped,

    // States
    isLoading,
    error,
    isCreating,

    // Pagination
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Actions
    onChatSelect: handleChatSelect,
    onChatDelete: handleChatDelete,
    createChat: handleCreateChat,

    // 🔗 Link semantiği refactor: tek kaynak path — StartNewChatButton.tsx
    // <Link to={newChatPath}> için burayı kullanıyor. `handleChatSelect` ve
    // `createChat` üstteki diğer tüketiciler (ConversationItem.tsx üzerinden
    // Conversations.tsx, HomeSidebarContent.tsx) için değiştirilmeden korunuyor.
    newChatPath: LAUNCHPAD_PATH,
  };
};
